import { resolveProtocolCodec, setProtocolCodec } from '@chompdome/shared/protocol';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createTRPCClient, trpc } from '@/lib/trpc';
import { isNativePlatform } from '@/native/platform';
import { restoreSessionId } from '@/native/session';
import { routeTree } from './routeTree.gen';
// Side-effect import: reads localStorage and stamps data-theme before the first paint.
import '@/stores/theme.store';
import './app.css';

/**
 * The wire format this tab ENCODES with. Binary unless `VITE_PROTOCOL_CODEC=json` — the same
 * switch the server reads, and safe to flip on one side alone, because decoding understands
 * either format whatever this is set to (`@chompdome/shared/protocol/codec.ts`).
 *
 * Set here, at the entrypoint, rather than in the game module: it is process-wide state, and
 * `/play` is loaded lazily, so a socket must not be the thing that decides it.
 */
setProtocolCodec(resolveProtocolCodec(import.meta.env.VITE_PROTOCOL_CODEC));

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            retry: 1,
          },
        },
      }),
  );

  const [trpcClient] = useState(() => createTRPCClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </trpc.Provider>
  );
}

function start(): void {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

/**
 * A native shell has to know its stored session id before the first request goes out, or the
 * `auth.me` that decides between the play button and the sign-in form is made anonymously and
 * the player looks signed out until something refetches. The Keychain read is the only thing
 * worth a frame here, and it happens on the shell alone: a browser has nothing to restore —
 * `restoreSessionId()` returns immediately — so the web path renders synchronously, exactly as
 * it did before.
 */
if (isNativePlatform()) {
  void restoreSessionId().finally(start);
} else {
  start();
}
